import { useEffect, useLayoutEffect, useMemo, useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { observer } from 'mobx-react-lite'
import { lanePositions, powerUpScaleEndDistance, powerUpScaleStartDistance } from '../../config/constants'
import { useRunState } from '../../provider/useRunState'
import { useTrackController } from '../track/useTrackController'
import { POWER_UP_COLLECTED_EVENT, type PowerUpCollectedDetail } from './powerUpEvents'
import { POWER_UP_CONFIG } from './powerUpConfig'
import { useShadowTexture } from '../../materials/shadowTexture'
import { getCollectablePowerUpPlacements } from './powerUpHelpers'

type ShadowInstance = {
    powerUpIndex: number
    position: number
    worldPosition: THREE.Vector3
    worldRotation: THREE.Quaternion
}

const SHADOW_SURFACE_OFFSET = 0.01

export const PowerUpShadows = observer(function PowerUpShadows() {
    const trackController = useTrackController()
    const runState = useRunState()
    const hiddenIndicesRef = useRef(new Set<number>())
    const meshRef = useRef<THREE.InstancedMesh | null>(null)
    const matrixRef = useRef(new THREE.Matrix4())
    const scaleRef = useRef(new THREE.Vector3())

    const placements = useMemo(() => getCollectablePowerUpPlacements(), [])
    const shadowTexture = useShadowTexture()
    const planeRotationFix = useMemo(
        () => new THREE.Quaternion().setFromEuler(
            new THREE.Euler(-Math.PI * 0.5, 0, 0),
        ),
        [],
    )

    const instances = useMemo<ShadowInstance[]>(
        () =>
            placements.map((placement) => {
                const frame = trackController.frameAtPosition(placement.position)
                const rotation = frame.cameraQuaternion.clone()
                const offset = new THREE.Vector3(
                    lanePositions[placement.track],
                    0,
                    0,
                ).applyQuaternion(rotation)
                const surfaceOffset = new THREE.Vector3(
                    0,
                    SHADOW_SURFACE_OFFSET,
                    0,
                ).applyQuaternion(rotation)

                return {
                    powerUpIndex: placement.powerUpIndex,
                    position: placement.position,
                    worldPosition: frame.position.clone().add(offset).add(surfaceOffset),
                    worldRotation: rotation.clone().multiply(planeRotationFix),
                }
            }),
        [placements, planeRotationFix, trackController],
    )

    useLayoutEffect(() => {
        const mesh = meshRef.current
        if (!mesh) {
            return
        }

        mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage)
        const matrix = new THREE.Matrix4()
        const zeroScale = new THREE.Vector3(0, 0, 0)
        instances.forEach((instance, index) => {
            matrix.compose(instance.worldPosition, instance.worldRotation, zeroScale)
            mesh.setMatrixAt(index, matrix)
        })
        mesh.instanceMatrix.needsUpdate = true
    }, [instances])

    useEffect(() => {
        const onCollected = (event: Event) => {
            const detail = (event as CustomEvent<PowerUpCollectedDetail>).detail
            if (!detail) return
            hiddenIndicesRef.current.add(detail.powerUpIndex)
        }

        window.addEventListener(POWER_UP_COLLECTED_EVENT, onCollected)
        return () => {
            window.removeEventListener(POWER_UP_COLLECTED_EVENT, onCollected)
        }
    }, [])

    useEffect(() => {
        if (runState.phase === 'idle') {
            hiddenIndicesRef.current.clear()
        }
    }, [runState.phase])

    useFrame(() => {
        const mesh = meshRef.current
        if (!mesh) {
            return
        }

        const trackPosition = trackController.getTrackPlaybackState().trackPosition
        const matrix = matrixRef.current
        const scale = scaleRef.current

        instances.forEach((instance, index) => {
            const deltaTrack = trackController.positionDeltaOnTrack(
                instance.position,
                trackPosition,
            )
            const blend = THREE.MathUtils.inverseLerp(
                powerUpScaleStartDistance,
                powerUpScaleEndDistance,
                deltaTrack,
            )
            const fadeScale =
                THREE.MathUtils.smoothstep(blend, 0, 1) * POWER_UP_CONFIG.baseScale

            const shadowScale = hiddenIndicesRef.current.has(instance.powerUpIndex) ? 0 : fadeScale

            matrix.compose(
                instance.worldPosition,
                instance.worldRotation,
                scale.setScalar(shadowScale),
            )
            mesh.setMatrixAt(index, matrix)
        })

        mesh.instanceMatrix.needsUpdate = true
    })

    return (
        <instancedMesh
            ref={meshRef}
            args={[undefined, undefined, placements.length]}
            frustumCulled={true}
        >
            <planeGeometry args={[2, 2]} />
            <meshBasicMaterial
                color='#000000'
                alphaMap={shadowTexture}
                transparent
                depthWrite
                opacity={POWER_UP_CONFIG.shadowOpacity}
                side={THREE.FrontSide}
                toneMapped={false}
            />
        </instancedMesh>
    )
})
